// Import necessary styles and d3 library
import './styles/graphic.scss';
import * as d3 from 'd3';

// Define a default export function that creates a treemap visualization
export default function (props, container, isMobile) {
  // Destructure properties from props
  const {
    data,
    initialWidth,
    initialHeight,
    // width,
    // height
  } = props;

  const offset = 55;
  const length = 5;

  const groupLabelAtTop = d => d.data.name == 'EU'
    || d.data.name == 'Other';

  const tileTopInset = 30;
  // Mobile Other: gap below EU tile bleed, then same header band as EU (y0 + 18 / + 27 …)
  const otherMobileSectionGap = tileTopInset;

  const groupHeaderOriginY = (d) => {
    if (isMobile == true && d.data.name == 'Other') {
      return d.y0 + otherMobileSectionGap;
    }
    return d.y0;
  };

  const leafVisualYOffset = (d) => {
    if (isMobile == true && d.parent.data.name == 'Other') {
      return otherMobileSectionGap;
    }
    return 0;
  };

  // Append an SVG element to the container with specified initial dimensions
  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  // Prepare input data in hierarchical format suitable for treemap visualization
  const root = d3.hierarchy(data).sum(d => d.value);

  // Compute treemap layout based on specified dimensions and padding
  d3.treemap()
    .size([initialWidth, initialHeight - 60])
    .paddingTop(5)
    .paddingRight(5)
    .paddingInner(2)
    .tile(d3.treemapSquarify.ratio(1.2))(root)
    .sort((a, b) => b.height - a.height || b.value - a.value);

  // Create a tooltip element for displaying node information on hover
  const tooltip = d3
    .select('body')
    .append('div')
    .style('position', 'absolute')
    .style('z-index', '10')
    .style('visibility', 'hidden')
    .style('background-color', 'white')
    .style('border', 'solid')
    .style('border-width', '1.5px')
    .style('border-color', '#5194C3')
    .style('border-radius', '5px')
    .style('padding', '5px');

  // Prepare a color scale for nodes based on group names
  const color = d3.scaleOrdinal()
    .domain(['United States', 'EU', 'Other'])
    .range(['#0b6490', '#5194C3', '#84c3f5']);

  const label_color = d3.scaleOrdinal()
    .domain(['United States', 'EU', 'Other'])
    .range(['#FFF', '#FFF', '#0b6490']);

  // Prepare an opacity scale for nodes based on value range
  const opacity = d3.scaleLinear()
    .domain([0, 0.5])
    .range([0.5, 1]);

  const labelPadding = 3;
  const nameToPercentGap = 16;
  const percentFontSize = 13;
  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');

  const measureTextWidth = (text, fontSizePx) => {
    measureCtx.font = `bold ${fontSizePx}px CNN, "Helvetica Neue", Helvetica, Arial, sans-serif`;
    return measureCtx.measureText(text).width;
  };

  const getLabelText = (d) => {
    if ((d.data.name) == 'United States' || (d.data.name) == 'United Kingdom' || (d.data.name) == 'Italy') {
      return d.data.name;
    } if ((String(d.data.name).length) > 10 && (d.data.pc) > 1.5) {
      return d.data.shortcode;
    } if (isMobile == true && (d.data.pc) < 3 && (d.data.pc) > 0.5) {
      return d.data.shortcode;
    } if ((d.data.pc) > 1.5) {
      return d.data.name;
    } if ((d.data.pc) < 1.5 && (d.data.pc) > 0.5) {
      return d.data.shortcode;
    }
    return '';
  };

  const getLabelFontSize = (d) => {
    if ((d.data.pc) > 40) {
      return 20;
    } if (isMobile == true) {
      return 13;
    }
    return 16;
  };

  const getLabelY = (d) => {
    const yShift = leafVisualYOffset(d);
    if (isMobile == true && (d.data.name) == 'United States') {
      return d.y0 + ((d.y1 + 10) - d.y0) / 2;
    } if ((d.data.pc) > 1) {
      return yShift + d.y0 + ((d.y1 + offset) - d.y0) / 2;
    }
    return yShift + d.y0 + ((d.y1 + 68) - d.y0) / 2;
  };

  const wrapLabelLines = (text, maxWidth, fontSizePx) => {
    if (!text || measureTextWidth(text, fontSizePx) <= maxWidth) {
      return [text];
    }

    const words = text.split(/\s+/);
    const lines = [];
    let line = words[0];

    for (let i = 1; i < words.length; i += 1) {
      const candidate = `${line} ${words[i]}`;
      if (measureTextWidth(candidate, fontSizePx) <= maxWidth) {
        line = candidate;
      } else {
        lines.push(line);
        line = words[i];
      }
    }
    lines.push(line);
    return lines;
  };

  const formatRoundedDecimal = (value) => {
    const rounded = Math.round(value * 10) / 10;
    if (Math.abs(rounded - Math.round(rounded)) < 0.001) {
      return String(Math.round(rounded));
    }
    return rounded.toFixed(1);
  };

  const formatAidAmount = (valueBillions) => {
    if (valueBillions < 1) {
      return `$${formatRoundedDecimal(valueBillions * 1000)} million`;
    }
    return `$${formatRoundedDecimal(valueBillions)} billion`;
  };

  const formatPercent = pc => `${formatRoundedDecimal(pc)}%`;

  // Add rectangles representing treemap nodes to the SVG
  svg
    .selectAll('rect')
    .data(root.leaves())
    .enter()
    .append('rect')
    .attr('rx', 5)
    .attr('ry', 5)
    .attr('x', d => d.x0)
    .attr('y', d => ((isMobile == true && (d.data.shortcode) == 'US') ? d.x0 + 10 : d.y0 + tileTopInset + leafVisualYOffset(d)))
    .attr('width', d => d.x1 - d.x0)
    .attr('height', d => d.y1 - d.y0)
    .style('fill', d => color(d.parent.data.name))
    .on('mouseover', () => {
      tooltip.style('visibility', 'visible');
    })
    .on('mousemove', (d) => {
      const tooltipWidth = tooltip.node().getBoundingClientRect().width;
      const tooltipHeight = tooltip.node().getBoundingClientRect().height;
      let tooltipX = d3.event.pageX - tooltipWidth / 2;
      let tooltipY = d3.event.pageY - tooltipHeight - 10;
      const parentRect = svg.node().getBoundingClientRect();
      if (tooltipX < parentRect.left) {
        tooltipX = parentRect.left;
      } else if (tooltipX + tooltipWidth > parentRect.right) {
        tooltipX = parentRect.right - tooltipWidth;
      }
      if (tooltipY < parentRect.top) {
        tooltipY = parentRect.top;
      } else if (tooltipY + tooltipHeight > parentRect.bottom) {
        tooltipY = parentRect.bottom - tooltipHeight;
      }
      tooltip
        .style('top', `${tooltipY}px`)
        .style('left', `${tooltipX}px`)
        .style('pointer-events', 'none')
        .html(`<b>${d.data.name}:</b> ${formatPercent(d.data.pc)}<br>
                ${formatAidAmount(d.data.value)}`);
    })
    .on('mouseout', () => {
      tooltip.style('visibility', 'hidden');
    });

  const appendNodeLabels = (selection) => {
    selection.each(function (d) {
      const label = getLabelText(d);
      const fontSizePx = getLabelFontSize(d);
      const maxWidth = (d.x1 - d.x0) - (labelPadding * 2);
      const lines = label ? wrapLabelLines(label, maxWidth, fontSizePx) : [];
      const lineHeight = fontSizePx * 1.05;
      const x = d.x0 + (d.x1 - d.x0) / 2;
      const y = getLabelY(d);
      // Stack extra name lines upward so wrapped text does not collide with % below
      const firstLineDy = lines.length <= 1 ? 0 : -((lines.length - 1) * lineHeight);

      const text = d3.select(this)
        .append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('font-size', `${fontSizePx}px`)
        .style('fill', label_color(d.parent.data.name))
        .style('pointer-events', 'none')
        .style('text-anchor', 'middle')
        .style('font-weight', 'bold');

      lines.forEach((line, i) => {
        text.append('tspan')
          .attr('x', x)
          .attr('dy', i === 0 ? `${firstLineDy}px` : `${lineHeight}px`)
          .text(line);
      });

      if ((d.data.pc) > 1) {
        text.append('tspan')
          .attr('x', x)
          .attr('dy', `${lines.length ? nameToPercentGap : firstLineDy}px`)
          .attr('font-size', `${percentFontSize}px`)
          .style('font-weight', 'normal')
          .text(formatPercent(d.data.pc));
      }
    });
  };

  // Country + percent labels (linked in one text block)
  svg
    .selectAll('g.node-label')
    .data(root.leaves().filter(d => getLabelText(d) || (d.data.pc) > 1))
    .enter()
    .append('g')
    .attr('class', 'node-label')
    .call(appendNodeLabels);

  // Add title text for the three main groups
  svg
    .selectAll('titles')
    .data(root.descendants().filter(d => d.depth == 1))
    .enter()
    .append('text')
    .attr('x', d => (d.x0 + d.x1) / 2)
    .attr('y', d => (groupLabelAtTop(d) ? groupHeaderOriginY(d) + 18 : d.y1 + offset))
    .text((d) => {
      if (d.data.name == 'United States') {
        return '';
      } if (d.data.name == 'EU') {
        return 'European Union: 52%';
      }
      return 'Other: 17%';
    })
    .attr('font-size', '14px')
    .style('text-anchor', 'middle')
    .attr('fill', '#262626');


  // Add the brackets for groups, each one is made up of three different lines. two end pieces and the horizontal
  svg.selectAll('hr')
    .data(root.descendants().filter(d => d.depth == 1 && d.data.name != 'United States'))
    .enter()
    .append('line')
    .style('stroke', '#BCBCBC')
    .style('stroke-width', 1)
    .attr('x1', d => d.x0 + 2)
    .attr('y1', d => (groupLabelAtTop(d) ? groupHeaderOriginY(d) + 27 : d.y1 + 40))
    .attr('x2', d => d.x1 - 7)
    .attr('y2', d => (groupLabelAtTop(d) ? groupHeaderOriginY(d) + 27 : d.y1 + 40));

  svg.selectAll('hr')
    .data(root.descendants().filter(d => d.depth == 1 && d.data.name != 'United States'))
    .enter()
    .append('line')
    .style('stroke', '#BCBCBC')
    .style('stroke-width', 1)
    .attr('x1', d => d.x0 + 2)
    .attr('y1', d => (groupLabelAtTop(d) ? groupHeaderOriginY(d) + ((offset / 2) + length) : d.y1 + 40))
    .attr('x2', d => d.x0 + 2)
    .attr('y2', d => (groupLabelAtTop(d) ? groupHeaderOriginY(d) + (offset / 2) : d.y1 + 34));

  svg.selectAll('hr')
    .data(root.descendants().filter(d => d.depth == 1 && d.data.name != 'United States'))
    .enter()
    .append('line')
    .style('stroke', '#BCBCBC')
    .style('stroke-width', 1)
    .attr('x1', d => d.x1 - 7)
    .attr('y1', d => (groupLabelAtTop(d) ? groupHeaderOriginY(d) + (offset / 2) : d.y1 + 40))
    .attr('x2', d => d.x1 - 7)
    .attr('y2', d => (groupLabelAtTop(d) ? groupHeaderOriginY(d) + ((offset / 2) + length) : d.y1 + 34));
}
